import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/client';
import { apiError } from './AdminSpvs';

const inr = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATEGORIES = ['legal', 'stamp_duty', 'registration', 'brokerage', 'audit', 'roc', 'site', 'bank', 'other'];

const AdminDistribution = () => {
  const { id } = useParams();
  const [expenses, setExpenses] = useState(null);
  const [dists, setDists] = useState([]);
  const [exp, setExp] = useState({ category: 'legal', amount: '', date: '', vendor: '', file: null });
  const [prev, setPrev] = useState({ gross_proceeds: '', payout_date: '', tds_pct: '' });

  const load = useCallback(async () => {
    try {
      const [e, d] = await Promise.all([api.get(`/api/admin/spvs/${id}/expenses/`), api.get(`/api/admin/spvs/${id}/distributions/`)]);
      setExpenses(e.data); setDists(d.data);
    } catch (err) {
      toast.error('Failed to load');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(apiError(e)); }
  };

  const addExpense = (e) => {
    e.preventDefault();
    const fd = new FormData();
    ['category', 'amount', 'date', 'vendor'].forEach((k) => fd.append(k, exp[k]));
    fd.append('invoice_file', exp.file);
    act(async () => { await api.post(`/api/admin/spvs/${id}/expenses/`, fd); setExp({ category: 'legal', amount: '', date: '', vendor: '', file: null }); }, 'Expense added');
  };
  const runPreview = (e) => {
    e.preventDefault();
    const body = { gross_proceeds: prev.gross_proceeds, payout_date: prev.payout_date };
    if (prev.tds_pct !== '') body.tds_pct = prev.tds_pct;
    act(() => api.post(`/api/admin/spvs/${id}/distributions/preview/`, body), 'Preview prepared');
  };
  const approve = (d) => window.confirm(`Approve distributing ${inr(d.distributable)}? Expenses will be frozen.`) &&
    act(() => api.post(`/api/admin/distributions/${d.id}/approve/`), 'Approved');
  const payout = (d, l) => {
    const utr = window.prompt(`Bank UTR for ${l.investor_name} (${inr(l.net)}):`, l.utr || '');
    if (utr) act(() => api.patch(`/api/admin/distributions/${d.id}/lines/${l.id}/`, { utr }), 'Payout recorded');
  };

  if (!expenses) return <main style={{ padding: '4rem 1rem', textAlign: 'center' }}><p>Loading…</p></main>;
  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <p><Link to={`/admin/spvs/${id}`}>← SPV</Link></p>
      <h1>Expenses and distribution</h1>

      <h2>Expenses (total {inr(expenses.total)})</h2>
      <ul>{expenses.expenses.map((e) => (
        <li key={e.id}>{e.date} · {e.category} · {e.vendor} · {inr(e.amount)}{' '}
          <button onClick={() => act(() => api.delete(`/api/admin/spvs/${id}/expenses/${e.id}/`), 'Removed')}>Remove</button></li>
      ))}</ul>
      <form onSubmit={addExpense} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select aria-label="Category" value={exp.category} onChange={(e) => setExp({ ...exp, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}</select>
        <input aria-label="Vendor" placeholder="Vendor" value={exp.vendor} onChange={(e) => setExp({ ...exp, vendor: e.target.value })} required />
        <input aria-label="Amount" type="number" step="0.01" placeholder="Amount (₹)" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} required />
        <input aria-label="Date" type="date" value={exp.date} onChange={(e) => setExp({ ...exp, date: e.target.value })} required />
        <input aria-label="Invoice" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setExp({ ...exp, file: e.target.files[0] })} required />
        <button type="submit" disabled={!exp.file}>Add expense (invoice required)</button>
      </form>

      <h2>Prepare distribution</h2>
      <p>The SPV must be in the <em>exiting</em> stage. Fees come from the SPV's own settings.</p>
      <form onSubmit={runPreview} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input aria-label="Gross proceeds" type="number" step="0.01" placeholder="Gross proceeds (₹)" value={prev.gross_proceeds} onChange={(e) => setPrev({ ...prev, gross_proceeds: e.target.value })} required />
        <input aria-label="Payout date" type="date" value={prev.payout_date} onChange={(e) => setPrev({ ...prev, payout_date: e.target.value })} required />
        <input aria-label="TDS percent" type="number" step="0.01" placeholder="TDS % (placeholder)" value={prev.tds_pct} onChange={(e) => setPrev({ ...prev, tds_pct: e.target.value })} />
        <button type="submit">Preview</button>
      </form>

      {dists.map((d) => (
        <section key={d.id} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, margin: '16px 0' }}>
          <h3 style={{ marginTop: 0 }}>{d.status === 'draft' ? 'Preview' : d.status} — rule {d.rule.rule}</h3>
          <dl>
            <dt>Gross proceeds</dt><dd>{inr(d.gross_proceeds)}</dd>
            <dt>Expenses</dt><dd>{inr(d.total_expenses)}</dd>
            <dt>Capital paid in</dt><dd>{inr(d.capital)}</dd>
            {d.investor_xirr_pct !== null && (<><dt>Investors' XIRR (before fee)</dt><dd>{d.investor_xirr_pct}%</dd></>)}
            <dt>{d.rule.rule === 'R3' ? 'Company fee' : 'Exit fee'}</dt><dd>{inr(d.company_fee)}</dd>
            <dt>Distributable</dt><dd>{inr(d.distributable)}</dd>
            <dt>Rounding remainder</dt><dd>{inr(d.rounding_remainder)}</dd>
          </dl>
          <table style={{ width: '100%' }}>
            <thead><tr><th align="left">Investor</th><th align="right">Shares</th><th align="right">Gross</th><th align="right">TDS ({d.tds_pct}%)</th><th align="right">Net</th><th align="left">UTR</th><th /></tr></thead>
            <tbody>{d.lines.map((l) => (
              <tr key={l.id}><td>{l.investor_name}</td><td align="right">{l.shares}</td><td align="right">{inr(l.gross)}</td><td align="right">{inr(l.tds)}</td><td align="right">{inr(l.net)}</td><td>{l.utr || '-'}</td>
                <td>{d.status !== 'draft' && <button onClick={() => payout(d, l)}>{l.utr ? 'Edit UTR' : 'Record payout'}</button>}</td></tr>
            ))}</tbody>
          </table>
          {d.status === 'draft' && <p><button onClick={() => approve(d)}>Approve distribution</button></p>}
        </section>
      ))}
    </main>
  );
};

export default AdminDistribution;
